import React from 'react';
import { CalendarRange } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu';
import { Button } from 'src/shared/components/ui/button';
import Tooltip from 'src/shared/components/tooltip/Tooltip';
import type { TimePeriodOption } from 'src/shared/utils/timePeriodHelpers';

type Props = {
    periods: TimePeriodOption[];
    selectedValue: string | null;
    onSelect: (value: string | null) => void;
};

const LATEST_SUFFIX = ' (latest)';
const NO_COMPARISON_TOOLTIP = 'No earlier data exists yet. Data will be refreshed each quarter.';

const TimePeriodFilter = ({ periods, selectedValue, onSelect }: Props) => {
    // periods is ordered latest-first by groupDatesIntoQuarters — the first entry represents "current".
    const currentPeriod = periods[0];
    const selectedPeriod = periods.find(p => p.value === selectedValue);
    const triggerLabel =
        selectedValue === null
            ? currentPeriod
                ? `${currentPeriod.label}${LATEST_SUFFIX}`
                : 'Time Period'
            : selectedPeriod?.label ?? 'Time Period';

    // Represent "current" in the menu via the first period's own value, so it is checked when
    // selectedValue is null (i.e. tracking live data rather than a pinned instant).
    const checkedValue = selectedValue ?? currentPeriod?.value ?? '';

    const handleChange = (value: string) => {
        onSelect(value === currentPeriod?.value ? null : value);
    };

    // Fewer than 2 periods means there's nothing earlier to compare against yet — disable rather
    // than open a menu with zero or one inert option, and explain why via a tooltip.
    const disabled = periods.length < 2;
    const trigger = (
        <DropdownMenuTrigger asChild>
            <Button variant='outline' disabled={disabled} data-testid='time-period-filter-button'>
                <CalendarRange width={16} height={16} aria-hidden />
                {triggerLabel}
            </Button>
        </DropdownMenuTrigger>
    );

    return (
        <DropdownMenu>
            {disabled ? (
                <Tooltip content={NO_COMPARISON_TOOLTIP} placement='bottom'>
                    {trigger}
                </Tooltip>
            ) : (
                trigger
            )}
            <DropdownMenuContent align='end' className='w-[240px]'>
                <DropdownMenuRadioGroup value={checkedValue} onValueChange={handleChange}>
                    {periods.map((p, index) => (
                        <DropdownMenuRadioItem key={p.value} value={p.value}>
                            {index === 0 ? `${p.label}${LATEST_SUFFIX}` : p.label}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default TimePeriodFilter;
